/**
 * 09 §5 training lane — the client-side step script. The server hosts the
 * lane and the TUTOR bot; this script drives the bot through
 * `TrainingBotMsg` commands and advances on event/position criteria. Pure.
 */
import type { SimEvent } from '../../../shared/events.js'
import type { TrainingBotMsg } from '../../../shared/protocol.js'

export type Device = 'mouse' | 'pad'
export const TRAINING_STEPS = 7
export const TRAINING_HINT_MS = 45_000
/** S10 step 7: a jump counts as a dodge once this long has passed since the bot's throw with no hit on the player. */
export const JUMP_DODGE_SETTLE_MS = 1000
export const VOLLEY_PERIOD_MS = 2500
export const SPORADIC_PERIOD_MS = 4000
export const DASH_CATCH_WINDOW_MS = 4000

const INSTRUCTIONS: Record<Device, string[]> = {
  mouse: ['MOVE — [WASD]', 'THROW — [LMB] aim with the cursor', 'BANK IT — hit the wall first. Follow the dashed line.', 'HOLD SHIELD — [RMB] face the disc', 'RAISE AS IT ARRIVES — send it back', 'DASH [Shift] — then stand still to CATCH your return', 'JUMP [Space] — as the disc arrives, let it pass under you'],
  pad: ['MOVE — left stick', 'THROW — [RT] aim with the right stick', 'BANK IT — hit the wall first. Follow the dashed line.', 'HOLD SHIELD — [LT] face the disc', 'RAISE AS IT ARRIVES — send it back', 'DASH [LB] — then stand still to CATCH your return', 'JUMP [A] — as the disc arrives, let it pass under you'],
}
const HINTS = [
  'walk left and right across the lane',
  'aim the reticle at the target and click',
  'aim at the wall beside the pillar, not at the target',
  'keep the shield up and face the incoming disc',
  'tap shield just as the disc reaches you',
  'throw, dash sideways, then stop before the disc returns',
  'time the jump so the disc passes under you',
]

export interface TrainingFacts { now: number; mePos: { x: number; z: number } | null; meAlive: boolean; events: SimEvent[]; localId: string; botId: string | null }

export class TrainingScript {
  step = 1
  done = false
  completedAt: number | null = null
  skipPending = false
  private stepStartedAt: number
  private lastPos: { x: number; z: number } | null = null
  private travelled = 0
  private touchedLeft = false
  private touchedRight = false
  private lateBlocks = 0
  private perfectDisc: number | null = null
  private flightDisc: number | null = null
  private dashAt: number | null = null
  /** S10 step 7: the bot's latest throw — did the player jump while it was in flight, and has it hit them? */
  private dodge: { discId: number; at: number; jumped: boolean } | null = null
  private nextThrowAt = 0
  private lastBotStance: TrainingBotMsg['stance'] | null = null

  constructor(readonly device: Device, now: number) { this.stepStartedAt = now }

  instruction(): string { return INSTRUCTIONS[this.device][this.step - 1] ?? '' }
  hint(now: number): string | null { return !this.done && now - this.stepStartedAt >= TRAINING_HINT_MS ? HINTS[this.step - 1] : null }
  progressRail(): string { return Array.from({ length: TRAINING_STEPS }, (_, i) => (i + 1 < this.step || this.done ? '■' : i + 1 === this.step ? `[${i + 1}]` : String(i + 1))).join(' ') }

  /** Bot command for this frame; `throw: true` pulses on the step cadence. Returns null when nothing changed. */
  botCommand(now: number): TrainingBotMsg | null {
    if (this.done) return null
    const stance: TrainingBotMsg['stance'] = this.step === 1 ? 'idle' : this.step === 2 ? 'stand' : this.step === 3 ? 'hide' : this.step === 6 ? 'sporadic' : 'volley' // steps 4, 5 and 7 volley
    let msg: TrainingBotMsg | null = null
    if (stance !== this.lastBotStance) { this.lastBotStance = stance; msg = { stance }; this.nextThrowAt = now + 1500 }
    const period = this.step === 6 ? SPORADIC_PERIOD_MS : this.step >= 4 ? VOLLEY_PERIOD_MS : 0
    if (period && now >= this.nextThrowAt) { this.nextThrowAt = now + period; msg = { ...(msg ?? {}), throw: true } }
    return msg
  }

  requestSkip(): void { if (!this.done) this.skipPending = true }
  cancelSkip(): void { this.skipPending = false }
  confirmSkip(now: number): void { if (this.skipPending) { this.skipPending = false; this.finish(now) } }

  private finish(now: number): void { this.done = true; this.completedAt = now; this.step = TRAINING_STEPS }
  private advance(now: number): void {
    if (this.step >= TRAINING_STEPS) { this.finish(now); return }
    this.step++
    this.stepStartedAt = now
    this.lateBlocks = 0; this.perfectDisc = null; this.flightDisc = null; this.dashAt = null; this.dodge = null
    this.nextThrowAt = now + 1500
  }

  /** Returns true when the step advanced this call. */
  observe(f: TrainingFacts): boolean {
    if (this.done) return false
    const mine = (id: string) => id === f.localId
    const bot = (id: string) => f.botId !== null && id === f.botId
    switch (this.step) {
      case 1: {
        if (f.mePos) {
          if (this.lastPos) this.travelled += Math.hypot(f.mePos.x - this.lastPos.x, f.mePos.z - this.lastPos.z)
          this.lastPos = f.mePos
          if (f.mePos.x < -2) this.touchedLeft = true
          if (f.mePos.x > 2) this.touchedRight = true
        }
        if (this.travelled >= 6 && this.touchedLeft && this.touchedRight) { this.advance(f.now); return true }
        return false
      }
      case 2:
      case 3:
        for (const e of f.events) if (e.type === 'hit' && mine(e.attackerId) && bot(e.victimId) && (this.step === 2 || e.bounceCount >= 1)) { this.advance(f.now); return true }
        return false
      case 4:
        for (const e of f.events) if (e.type === 'deflectLate' && mine(e.playerId)) this.lateBlocks++
        if (this.lateBlocks >= 2) { this.advance(f.now); return true }
        return false
      case 5:
        for (const e of f.events) {
          if (e.type === 'deflectPerfect' && mine(e.playerId)) this.perfectDisc = e.discId
          if (e.type === 'hit' && bot(e.victimId) && this.perfectDisc === e.discId) { this.advance(f.now); return true }
        }
        return false
      case 6:
        for (const e of f.events) {
          if (e.type === 'throw' && mine(e.playerId)) { this.flightDisc = e.discId; this.dashAt = null }
          if (e.type === 'dash' && mine(e.playerId) && this.flightDisc !== null) this.dashAt = f.now
          if (e.type === 'catch' && mine(e.playerId) && e.discId === this.flightDisc && this.dashAt !== null && f.now - this.dashAt <= DASH_CATCH_WINDOW_MS) { this.advance(f.now); return true }
        }
        return false
      case 7:
        // S10 (09 §5 step 7): the bot volleys on the lane cadence; a `jump` by the player while the bot's disc is in
        // flight, with no `hit` on the player from that disc within JUMP_DODGE_SETTLE_MS of the throw, completes the lane.
        for (const e of f.events) {
          if (e.type === 'throw' && bot(e.playerId)) this.dodge = { discId: e.discId, at: f.now, jumped: false }
          if (e.type === 'jump' && mine(e.playerId) && this.dodge) this.dodge.jumped = true
          if (e.type === 'hit' && mine(e.victimId) && this.dodge && e.discId === this.dodge.discId) this.dodge = null
        }
        if (this.dodge && this.dodge.jumped && f.now - this.dodge.at >= JUMP_DODGE_SETTLE_MS) { this.dodge = null; this.advance(f.now); return true }
        return false
      default: return false
    }
  }
}
